import {useEffect, useMemo, useRef} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {ISelectedRegion} from "../../../offer/list/actions/offer_list_selected_data_actions";
import {disableDynamicPositionProps} from "../../../offer/list/hooks/use_list_dynamic_positions";
import {OfferType} from "../../../offer/types/OfferType";
import {useGetRegionDetailListQuery} from "../../../region/api/get_region_detail_list";
import {RegionType} from "../../../region/types/RegionType";
import {useAppDispatch} from "../../../store/hooks";
import {useElementViewportShowingUpStatus} from "../../../tracking/hooks/use_element_viewport_showing_up_status";
import {openForYouModal} from "../../state/for_you_modal_slice";
import {ForYouBannerGtmEvent} from "../../tracking/for_you_banner_gtm_events";
import {ForYouOpenType} from "../../types/ForYouOpenType";
import {ForYouBannerBenefitItem} from "./ForYouBannerBenefitItem";
import {ForYouBannerConsultantsAvatars} from "./ForYouBannerConsultantsAvatars";

interface IProps {
    className?: string;
    offerTypes: OfferType[];
    selectedRegion: ISelectedRegion[];
}

export const ForYouBanner = (props: IProps) => {
    const dispatch = useAppDispatch();

    const {data: regions} = useGetRegionDetailListQuery(
        {regionIds: props.selectedRegion.map((region) => region.id)},
        {skip: props.selectedRegion.length === 0}
    );

    const regionCity = useMemo(() => {
        const cityInRegions = regions?.length && regions.find((region) => region.type === RegionType.CITY);
        if (cityInRegions) {
            return cityInRegions.name;
        }
        if (regions) {
            return regions[0]?.parent?.name || "";
        }

        return "";
    }, [regions]);

    const {containerId, isWaitingForElement} = useElementViewportShowingUpStatus({useVisibilityTracking: true});
    const wasViewTracked = useRef(false);
    const isWaitingForRegionCity = props.selectedRegion.length > 0 && !regionCity && !regions;

    useEffect(() => {
        if (isWaitingForElement || isWaitingForRegionCity || wasViewTracked.current) {
            return;
        }

        wasViewTracked.current = true;
        ForYouBannerGtmEvent(regionCity, ForYouOpenType.BANNER, "view");
    }, [isWaitingForElement, isWaitingForRegionCity, regionCity]);

    const handleFormButtonClick = () => {
        ForYouBannerGtmEvent(regionCity, ForYouOpenType.BANNER);
        dispatch(openForYouModal({types: props.offerTypes, openFrom: ForYouOpenType.BANNER, source: PreferencesSurveySource.FOR_YOU_BANNER}));
    };

    return (
        <li className={cx(containerStyle, props.className)} {...disableDynamicPositionProps()}>
            <section className={bannerWrapperStyle} data-id={containerId} data-testid={FOR_YOU_TEST_IDS.FOR_YOU_BANNER}>
                <ForYouBannerConsultantsAvatars />

                <div className={contentWrapperStyle}>
                    <Text variant="headline_3" as="p" className={headlineStyle}>
                        Nie chcesz szukać samodzielnie?
                    </Text>

                    <Text variant="body_copy_1" as="p" className={descriptionStyle}>
                        Zostaw kontakt, a doradca przygotuje dla Ciebie zestawienie mieszkań.
                    </Text>

                    <ul className={benefitsListStyle}>
                        <ForYouBannerBenefitItem>
                            <strong>Bezpłatnie</strong> wyselekcjonujemy mieszkania idealnie dopasowane do Twoich potrzeb.
                        </ForYouBannerBenefitItem>
                        <ForYouBannerBenefitItem>
                            <strong>Oszczędzimy Twój czas</strong>, odrzucając nietrafione ogłoszenia.
                        </ForYouBannerBenefitItem>
                        <ForYouBannerBenefitItem>
                            <strong>Zapewnimy Ci wsparcie</strong> doradcy mieszkaniowego na każdym etapie.
                        </ForYouBannerBenefitItem>
                    </ul>
                </div>

                <div className={actionsWrapperStyle}>
                    <Button variant="filled_secondary" onClick={handleFormButtonClick} dataTestId={FOR_YOU_TEST_IDS.FOR_YOU_BANNER_BUTTON}>
                        Zamów rozmowę
                    </Button>
                </div>
            </section>
        </li>
    );
};

const containerStyle = css({
    flex: "0 0 100%",
    px: 1.5,
    listStyle: "none"
});

const bannerWrapperStyle = css({
    position: "relative",
    overflow: "hidden",
    backgroundColor: "var(--colors-white)",
    px: 3,
    py: 3,
    borderRadius: 2,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "center",
    justifyItems: "center",
    gap: 3,
    maxWidth: 375,
    margin: "auto",
    boxShadow: "var(--shadow-2)",

    md: {
        maxWidth: "unset",
        gap: 4,
        gridTemplateColumns: "auto minmax(0, 1fr)",
        justifyItems: "start"
    },

    lg: {
        gridTemplateColumns: "auto minmax(0, 1fr) auto"
    }
});

const contentWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    maxWidth: 560,

    md: {
        alignItems: "start",
        gap: 2,
        maxWidth: "unset"
    },

    lg: {
        minWidth: 0
    },

    xl: {
        alignSelf: "center"
    }
});

const headlineStyle = css({
    color: "var(--colors-secondary)",
    maxWidth: "100%",
    textAlign: "center",

    md: {
        textAlign: "left"
    },

    lg: {
        maxWidth: "unset"
    }
});

const descriptionStyle = css({
    color: "var(--colors-secondary)",
    textAlign: "center",
    m: 0,

    md: {
        textAlign: "left"
    }
});

const benefitsListStyle = css({
    m: 0,
    p: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: 3,

    md: {
        gap: 1.5
    },

    xl: {
        gap: 2
    }
});

const actionsWrapperStyle = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1.5,

    md: {
        gridColumn: "2",
        width: "auto",
        minWidth: 280,
        justifySelf: "start",
        flexDirection: "row"
    },

    lg: {
        flexDirection: "column",
        gridColumn: "auto",
        minWidth: 260,
        justifySelf: "end"
    }
});
import {useEffect, useMemo, useRef} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {Text} from "@pg-design/text-module";
import {FOR_YOU_TEST_IDS} from "@pg-mono/test-ids";
import {PreferencesSurveySource} from "@pg-rp/common-types";

import {ISelectedRegion} from "../../../offer/list/actions/offer_list_selected_data_actions";
import {disableDynamicPositionProps} from "../../../offer/list/hooks/use_list_dynamic_positions";
import {OfferType} from "../../../offer/types/OfferType";
import {useGetRegionDetailListQuery} from "../../../region/api/get_region_detail_list";
import {RegionType} from "../../../region/types/RegionType";
import {useAppDispatch} from "../../../store/hooks";
import {useElementViewportShowingUpStatus} from "../../../tracking/hooks/use_element_viewport_showing_up_status";
import {openForYouModal} from "../../state/for_you_modal_slice";
import {ForYouBannerGtmEvent} from "../../tracking/for_you_banner_gtm_events";
import {ForYouOpenType} from "../../types/ForYouOpenType";
import {ForYouBannerBenefitItem} from "./ForYouBannerBenefitItem";
import {ForYouBannerConsultantsAvatars} from "./ForYouBannerConsultantsAvatars";

interface IProps {
    className?: string;
    offerTypes: OfferType[];
    selectedRegion: ISelectedRegion[];
}

export const ForYouBanner = (props: IProps) => {
    const dispatch = useAppDispatch();

    const {data: regions} = useGetRegionDetailListQuery(
        {regionIds: props.selectedRegion.map((region) => region.id)},
        {skip: props.selectedRegion.length === 0}
    );

    const regionCity = useMemo(() => {
        const cityInRegions = regions?.length && regions.find((region) => region.type === RegionType.CITY);
        if (cityInRegions) {
            return cityInRegions.name;
        }
        if (regions) {
            return regions[0]?.parent?.name || "";
        }

        return "";
    }, [regions]);

    const {containerId, isWaitingForElement} = useElementViewportShowingUpStatus({useVisibilityTracking: true});
    const wasViewTracked = useRef(false);
    const isWaitingForRegionCity = props.selectedRegion.length > 0 && !regionCity && !regions;

    useEffect(() => {
        if (isWaitingForElement || isWaitingForRegionCity || wasViewTracked.current) {
            return;
        }

        wasViewTracked.current = true;
        ForYouBannerGtmEvent(regionCity, ForYouOpenType.BANNER, "view");
    }, [isWaitingForElement, isWaitingForRegionCity, regionCity]);

    const handleFormButtonClick = () => {
        ForYouBannerGtmEvent(regionCity, ForYouOpenType.BANNER);
        dispatch(openForYouModal({types: props.offerTypes, openFrom: ForYouOpenType.BANNER, source: PreferencesSurveySource.FOR_YOU_BANNER}));
    };

    return (
        <li className={cx(containerStyle, props.className)} {...disableDynamicPositionProps()}>
            <section className={bannerWrapperStyle} data-id={containerId} data-testid={FOR_YOU_TEST_IDS.FOR_YOU_BANNER}>
                <ForYouBannerConsultantsAvatars />

                <div className={contentWrapperStyle}>
                    <Text variant="headline_3" as="p" className={headlineStyle}>
                        Nie chcesz szukać samodzielnie?
                    </Text>

                    <Text variant="body_copy_1" as="p" className={descriptionStyle}>
                        Zostaw kontakt, a doradca przygotuje dla Ciebie zestawienie mieszkań.
                    </Text>

                    <ul className={benefitsListStyle}>
                        <ForYouBannerBenefitItem>
                            <strong>Bezpłatnie</strong> wyselekcjonujemy mieszkania idealnie dopasowane do Twoich potrzeb.
                        </ForYouBannerBenefitItem>
                        <ForYouBannerBenefitItem>
                            <strong>Oszczędzimy Twój czas</strong>, odrzucając nietrafione ogłoszenia.
                        </ForYouBannerBenefitItem>
                        <ForYouBannerBenefitItem>
                            <strong>Zapewnimy Ci wsparcie</strong> doradcy mieszkaniowego na każdym etapie.
                        </ForYouBannerBenefitItem>
                    </ul>
                </div>

                <div className={actionsWrapperStyle}>
                    <Button variant="filled_secondary" onClick={handleFormButtonClick} dataTestId={FOR_YOU_TEST_IDS.FOR_YOU_BANNER_BUTTON}>
                        Zamów rozmowę
                    </Button>
                </div>
            </section>
        </li>
    );
};

const containerStyle = css({
    flex: "0 0 100%",
    px: 1.5,
    listStyle: "none"
});

const bannerWrapperStyle = css({
    position: "relative",
    overflow: "hidden",
    backgroundColor: "var(--colors-white)",
    px: 3,
    py: 3,
    borderRadius: 2,
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    alignItems: "center",
    justifyItems: "center",
    gap: 3,
    maxWidth: 375,
    margin: "auto",
    boxShadow: "var(--shadow-2)",

    md: {
        maxWidth: "unset",
        gap: 4,
        gridTemplateColumns: "auto minmax(0, 1fr)",
        justifyItems: "start"
    },

    lg: {
        gridTemplateColumns: "auto minmax(0, 1fr) auto"
    }
});

const contentWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    maxWidth: 560,

    md: {
        alignItems: "start",
        gap: 2,
        maxWidth: "unset"
    },

    lg: {
        minWidth: 0
    },

    xl: {
        alignSelf: "center"
    }
});

const headlineStyle = css({
    color: "var(--colors-secondary)",
    maxWidth: "100%",
    textAlign: "center",

    md: {
        textAlign: "left"
    },

    lg: {
        maxWidth: "unset"
    }
});

const descriptionStyle = css({
    color: "var(--colors-secondary)",
    textAlign: "center",
    m: 0,

    md: {
        textAlign: "left"
    }
});

const benefitsListStyle = css({
    m: 0,
    p: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: 3,

    md: {
        gap: 1.5
    },

    xl: {
        gap: 2
    }
});

const actionsWrapperStyle = css({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    gap: 1.5,

    md: {
        gridColumn: "2",
        width: "auto",
        minWidth: 280,
        justifySelf: "start",
        flexDirection: "row"
    },

    lg: {
        flexDirection: "column",
        gridColumn: "auto",
        minWidth: 260,
        justifySelf: "end"
    }
});
